import { useTranslation } from "react-i18next";

import { Tablist } from "@/components/Interface/Tablist/Tablist";
import {
  DeviceTabHints,
  TabDisabledState,
} from "@/components/Portal/DeviceManagement/deviceManagementTabs.ts";
import { SimCardDetailSimStatus } from "@/services/model";

interface DeviceManagementTabsProps {
  /** The `simId` the tab targets link to. */
  simId: string;
  /** Status of the displayed SIM; an orange SIM gains a Number Porting tab. */
  simStatus: SimCardDetailSimStatus | undefined;
  /** Which tabs are disabled for the current flow and SIM. */
  tabState: TabDisabledState;
  /** The hints shown on disabled tabs. */
  hints: DeviceTabHints;
  /** The tariff is a Flat Home product; Roaming/Voice/Watch SIM are hidden. */
  isFlatHome: boolean;
  /** The tariff is data-only; the Voice tab is hidden. */
  isDataOnly: boolean;
  /** Whether the customer may book voice options at all. */
  canBookVoiceOption: boolean;
}

/**
 * Renders the device management tab strip. It assembles the tab list from the
 * pre-computed disable/visibility state and hints; the branching that decides
 * those values lives in {@link ../deviceTabState}.
 */
export const DeviceManagementTabs = ({
  simId,
  simStatus,
  tabState,
  hints,
  isFlatHome,
  isDataOnly,
  canBookVoiceOption,
}: DeviceManagementTabsProps) => {
  const { t } = useTranslation("portal");

  const numberPortingTab = {
    target: `number-porting?simId=${simId}`,
    disabled: false,
    text: t("cockpit.managementTile.tabs.numberPorting.title"),
  };

  return (
    <Tablist
      tabs={[
        ...(simStatus === SimCardDetailSimStatus.orange
          ? [numberPortingTab]
          : []),
        {
          target: `subscription?simId=${simId}`,
          disabled: tabState.isTariffDisabled,
          disabledHint: hints.numberPortingHint ?? hints.inactiveSimHint,
          text: t("cockpit.managementTile.tabs.abo"),
        },
        {
          target: `roaming?simId=${simId}`,
          disabled: tabState.isRoamingDisabled,
          hidden: isFlatHome,
          disabledHint: hints.numberPortingHint ?? hints.inactiveSimHint,
          text: t("cockpit.managementTile.tabs.roaming"),
        },
        {
          target: `voice?simId=${simId}`,
          disabled: tabState.isVoiceDisabled,
          hidden: !canBookVoiceOption || isFlatHome || isDataOnly,
          disabledHint: hints.noSubscriptionHint,
          text: t("cockpit.managementTile.tabs.voice"),
        },
        {
          target: `watch-sim?simId=${simId}`,
          disabled: tabState.isWatchSimTabDisabled,
          hidden: !canBookVoiceOption || isFlatHome,
          disabledHint: hints.inactiveSimHint,
          text: t("cockpit.managementTile.tabs.watchSim"),
        },
        {
          target: `details?simId=${simId}`,
          disabled: tabState.isDetailsDisabled,
          disabledHint: hints.inactiveSimHint,
          text: t("cockpit.managementTile.tabs.details"),
        },
      ]}
    />
  );
};
